import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { normalizeImageUrl } from "../lib/imageUrl";
import { FacebookLogo, InstagramLogo, TiktokLogo, YoutubeLogo, XLogo, EnvelopeSimple, Phone, MapPin } from "@phosphor-icons/react";

export default function Footer() {
  const [s, setS] = useState(null);
  const [nlEmail, setNlEmail] = useState("");
  const [nlName, setNlName] = useState("");
  const [nlState, setNlState] = useState("idle"); // idle | sending | done | error
  useEffect(() => { api.get("/public/home-settings").then(r => setS(r.data)).catch(() => {}); }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!nlEmail.trim() || nlState === "sending") return;
    setNlState("sending");
    try {
      await api.post("/public/newsletter", { email: nlEmail.trim(), name: nlName.trim() });
      setNlState("done");
      setNlEmail("");
      setNlName("");
    } catch {
      setNlState("error");
    }
  };

  const socials = [
    { url: s?.footer_facebook_url, Icon: FacebookLogo, label: "Facebook" },
    { url: s?.footer_instagram_url, Icon: InstagramLogo, label: "Instagram" },
    { url: s?.footer_tiktok_url, Icon: TiktokLogo, label: "TikTok" },
    { url: s?.footer_youtube_url, Icon: YoutubeLogo, label: "YouTube" },
    { url: s?.footer_x_url, Icon: XLogo, label: "X" },
  ].filter(x => x.url);

  return (
    <footer className="bg-parchment text-ms-ink mt-24" data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12">
        {/* TOP — logo + tagline + quote, centered editorial */}
        <div className="flex flex-col items-center text-center">
          {s?.footer_logo ? (
            <img src={normalizeImageUrl(s.footer_logo)} alt="Mystery Served" className="h-32 w-auto object-contain" data-testid="footer-logo" />
          ) : (
            <p className="serif text-4xl text-ms-ink">Mystery <span className="italic text-ms-brass">Served</span></p>
          )}

          {s?.footer_tagline && (
            <p className="serif italic text-ms-ink text-xl md:text-2xl mt-8 max-w-xl" data-testid="footer-tagline">{s.footer_tagline}</p>
          )}

          {s?.footer_quote && (
            <blockquote className="mt-4 max-w-2xl text-ms-stone leading-[1.7] text-base italic" data-testid="footer-quote">
              "{s.footer_quote}"
              {s.footer_quote_attribution && <span className="not-italic text-ms-brass text-sm ml-2">— {s.footer_quote_attribution}</span>}
            </blockquote>
          )}
        </div>

        {/* MIDDLE — three editorial columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 mt-16">
          <div>
            <p className="label-eyebrow text-ms-brass">Visit</p>
            <ul className="mt-5 space-y-3 text-sm">
              <li><Link to="/tours" className="text-ms-ink/80 hover:text-ms-brass" data-testid="footer-link-tours">Tours</Link></li>
              <li><Link to="/dates" className="text-ms-ink/80 hover:text-ms-brass" data-testid="footer-link-dates">Book</Link></li>
              <li><Link to="/gift-cards" className="text-ms-ink/80 hover:text-ms-brass" data-testid="footer-link-gift-cards">Gift cards</Link></li>
              <li><Link to="/about" className="text-ms-ink/80 hover:text-ms-brass" data-testid="footer-link-about">About</Link></li>
              <li><Link to="/partners" className="text-ms-ink/80 hover:text-ms-brass" data-testid="footer-link-partners">Partner</Link></li>
              <li><Link to="/privacy" className="text-ms-ink/80 hover:text-ms-brass" data-testid="footer-link-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-ms-ink/80 hover:text-ms-brass" data-testid="footer-link-terms">Terms of Service</Link></li>
              <li><a href="/mystery-adventures-st-petersburg.html" className="text-ms-ink/80 hover:text-ms-brass" data-testid="footer-link-seo-page">St. Pete Mystery Adventures</a></li>
            </ul>
          </div>

          <div>
            <p className="label-eyebrow text-ms-brass">Contact</p>
            <ul className="mt-5 space-y-3 text-sm">
              {s?.footer_email && (
                <li>
                  <a href={`mailto:${s.footer_email}`} className="inline-flex items-center gap-2 text-ms-ink/80 hover:text-ms-brass break-all" data-testid="footer-email">
                    <EnvelopeSimple size={14} weight="duotone" /> {s.footer_email}
                  </a>
                </li>
              )}
              {s?.footer_phone && (
                <li>
                  <a href={`tel:${s.footer_phone.replace(/[^+\d]/g, "")}`} className="inline-flex items-center gap-2 text-ms-ink/80 hover:text-ms-brass" data-testid="footer-phone">
                    <Phone size={14} weight="duotone" /> {s.footer_phone}
                  </a>
                </li>
              )}
              {s?.footer_address && (
                <li className="flex items-start gap-2 text-ms-ink/80" data-testid="footer-address">
                  <MapPin size={14} weight="duotone" className="mt-0.5 shrink-0" />
                  <span className="whitespace-pre-line">{s.footer_address}</span>
                </li>
              )}
            </ul>
          </div>

          <div>
            <p className="label-eyebrow text-ms-brass">Stay in the loop</p>
            <p className="mt-5 text-sm text-ms-stone leading-[1.7]">Be first to know when new mysteries drop.</p>
            {nlState === "done" ? (
              <p className="mt-4 text-sm text-ms-brass" data-testid="footer-newsletter-success">You're in. Watch your inbox for new mysteries.</p>
            ) : (
              <form className="mt-4 flex flex-col gap-2" onSubmit={subscribe}>
                <input
                  type="text"
                  placeholder="First name (optional)"
                  value={nlName}
                  onChange={e => setNlName(e.target.value)}
                  maxLength={40}
                  className="bg-white border border-ms-edge rounded-md px-3 py-2.5 text-sm text-ms-ink placeholder-ms-mist focus:outline-none focus:border-ms-brass"
                  data-testid="footer-newsletter-name"
                />
                <div className="flex gap-2">
                  <input type="email" required placeholder="email@example.com" value={nlEmail} onChange={e => setNlEmail(e.target.value)} className="flex-1 bg-white border border-ms-edge rounded-md px-3 py-2.5 text-sm text-ms-ink placeholder-ms-mist focus:outline-none focus:border-ms-brass" data-testid="footer-newsletter-email" />
                  <button type="submit" disabled={nlState === "sending"} className="btn-primary-dark !text-[11px] !py-2.5 !px-5 disabled:opacity-60" data-testid="footer-newsletter-submit">{nlState === "sending" ? "..." : "Join"}</button>
                </div>
              </form>
            )}
            {nlState === "error" && (
              <p className="mt-2 text-xs text-ms-danger" data-testid="footer-newsletter-error">Something went wrong — please check your email and try again.</p>
            )}
            {socials.length > 0 && (
              <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
                {socials.map(({ url, Icon, label }) => (
                  <a key={label} href={url} target="_blank" rel="noreferrer" aria-label={label}
                     className="w-9 h-9 inline-flex items-center justify-center rounded-full bg-ms-ink text-ms-cream hover:bg-ms-brass transition-colors"
                     data-testid={`footer-social-${label.toLowerCase()}`}>
                    <Icon size={16} weight="bold" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="hairline mt-16" />
        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 text-xs text-ms-stone">
          <p>© {new Date().getFullYear()} {s?.footer_copyright || "Mystery Served. All rights reserved."}</p>
          {s?.footer_terms_label && s?.footer_terms_url && (
            <a href={s.footer_terms_url} className="text-ms-stone hover:text-ms-brass underline underline-offset-4" data-testid="footer-terms">
              {s.footer_terms_label}
            </a>
          )}
        </div>
      </div>
    </footer>
  );
}
